import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRotateRight,
  faCheck,
  faGripVertical,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from '@/lib/swal';
import {
  inspectionReportService,
  type ReportSectionColumn,
  type ReportSectionComposition,
  type ReportSectionCompositionItem,
} from '@/services/inspection-report.service';
import { SectionWidthEditor } from './SectionWidthEditor';
import { sameWidths, widthsOf } from './column-width';

interface Props {
  inspectionId: string;
  canEdit: boolean;
  /**
   * Suppresses the composer's own "Report Sections" heading for callers that
   * already title the block themselves — the collapsible panel on the report
   * screen. Everything else, including the selection count, is unaffected.
   */
  hideHeading?: boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function isForbidden(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

/** The template supplies a title, or null meaning "use the renderer's own". */
function labelOf(section: ReportSectionCompositionItem): string {
  return section.title ?? section.defaultTitle ?? section.sectionKey;
}

/**
 * Two compositions are the same when the sections appear in the same order with
 * the same visibility. Position numbers are derived from the array index, so
 * comparing them separately would flag a report as dirty purely because the
 * server renumbered — which is exactly the false-positive B5.3 had to fix.
 */
function sameComposition(
  a: readonly ReportSectionCompositionItem[],
  b: readonly ReportSectionCompositionItem[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (s, i) =>
      s.sectionKey === b[i]!.sectionKey &&
      s.isVisible === b[i]!.isVisible &&
      // Exact width comparison: the server stores an override only when a width
      // actually differs, so tolerating "close enough" would report an
      // untouched report as edited, and rounding would hide a real edit.
      sameWidths(widthsOf(s.columns), widthsOf(b[i]!.columns)),
  );
}

function SortableSection({
  section,
  index,
  canEdit,
  busy,
  onToggle,
  onResize,
}: {
  section: ReportSectionCompositionItem;
  index: number;
  canEdit: boolean;
  busy: boolean;
  onToggle: () => void;
  onResize: (columns: ReportSectionColumn[]) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionKey,
    disabled: !canEdit,
  });
  const label = labelOf(section);

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="rounded-lg border border-border bg-card px-3 py-2"
      data-testid={`report-section-${section.sectionKey}`}
    >
      <div className="flex items-center gap-2">
        <span className="w-5 shrink-0 text-xs font-semibold text-muted-foreground">
          {index + 1}.
        </span>

        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
          {/* Real checkbox: keyboard-operable and properly labelled. The tick and
            the "Hidden" chip both carry the state, so it is never colour alone. */}
          <input
            type="checkbox"
            className="peer sr-only"
            checked={section.isVisible}
            disabled={!canEdit || busy}
            onChange={onToggle}
            aria-label={`Include section ${label}`}
            data-testid={`toggle-section-${section.sectionKey}`}
          />
          <span
            aria-hidden="true"
            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
              section.isVisible
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border'
            } peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-ring`}
          >
            {section.isVisible && <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5" />}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
        </label>

        {!section.isVisible && (
          <span
            className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            data-testid={`section-hidden-${section.sectionKey}`}
          >
            Hidden
          </span>
        )}

        {canEdit && (
          <button
            type="button"
            className="shrink-0 cursor-grab rounded px-1 py-1 text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={`Reorder ${label}`}
            data-testid={`drag-section-${section.sectionKey}`}
            {...attributes}
            {...listeners}
          >
            <FontAwesomeIcon icon={faGripVertical} className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Resizability is the API's verdict, never inferred from the section key.
          A section drawn as one full-width block has nothing to resize. */}
      {section.resizable && (
        <SectionWidthEditor
          sectionKey={section.sectionKey}
          sectionLabel={label}
          columns={section.columns}
          canEdit={canEdit}
          onChange={onResize}
        />
      )}
    </li>
  );
}

/**
 * Report Section Composer — decides WHICH sections appear in the report and in
 * WHAT order. Follows ReportFindingSelector's visual language, @dnd-kit
 * ordering and local-draft-then-Save behaviour.
 *
 * The API response is the only source of truth for the section list: the
 * component never reconstructs the template, never merges overrides, and never
 * builds the sparse document. It sends the complete composition and re-seeds
 * itself from whatever the server returns.
 *
 * Editing stops at generation. Once a report is generated its layout is frozen
 * into a snapshot, so a composition change could no longer reach the PDF —
 * the editor goes read-only rather than accepting edits that would do nothing.
 */
export function ReportSectionComposer({ inspectionId, canEdit, hideHeading }: Props) {
  const qc = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Keyboard reordering: Space to lift, arrows to move, Space to drop.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const query = useQuery({
    queryKey: ['report-sections', inspectionId],
    queryFn: () => inspectionReportService.getSectionComposition(inspectionId),
    retry: false,
    staleTime: 10_000,
  });

  const composition = query.data;

  /** Working draft: the ordered section list. Not persisted until Save. */
  const [draft, setDraft] = useState<ReportSectionCompositionItem[] | null>(null);
  /** The last state the server confirmed — what Cancel restores and dirty compares against. */
  const [saved, setSaved] = useState<ReportSectionCompositionItem[] | null>(null);
  /** Set when the server refuses the write specifically on permission grounds. */
  const [saveForbidden, setSaveForbidden] = useState(false);

  useEffect(() => {
    if (!composition) return;
    setSaved(composition.sections);
    // Never clobber edits in progress: a background refetch must not discard
    // what the engineer is part-way through changing.
    setDraft((current) => current ?? composition.sections);
  }, [composition]);

  const saveMutation = useMutation({
    mutationFn: (sections: ReportSectionCompositionItem[]) =>
      inspectionReportService.setSectionComposition(
        inspectionId,
        composition?.version ?? 1,
        sections.map((s, index) => ({
          sectionKey: s.sectionKey,
          isVisible: s.isVisible,
          // Position follows the displayed order; the backend re-normalises.
          position: index,
          // Widths only where the renderer can honour them — the API rejects
          // them for anything else, and sending them would be a lie anyway.
          ...(s.resizable ? { columnWidths: widthsOf(s.columns) } : {}),
        })),
      ),
    onSuccess: (result: ReportSectionComposition) => {
      // The server's response becomes the new truth, not the local draft.
      setDraft(result.sections);
      setSaved(result.sections);
      setSaveForbidden(false);
      qc.setQueryData(['report-sections', inspectionId], result);
      toast.success('Report sections updated');
    },
    onError: (e: Error) => {
      // The draft is deliberately left intact so the engineer's work survives.
      if (isForbidden(e)) setSaveForbidden(true);
      toast.error(extractMsg(e, 'Failed to update report sections'));
    },
  });

  const busy = saveMutation.isPending;

  // Every mutator below is a no-op on a section the template owns. The backend
  // rejects such a change anyway; refusing it here as well means the UI can
  // never build a payload the server will turn down.
  const toggle = (sectionKey: string) =>
    setDraft((cur) =>
      (cur ?? []).map((s) =>
        s.sectionKey === sectionKey && s.isEngineerToggleable
          ? { ...s, isVisible: !s.isVisible }
          : s,
      ),
    );

  const setAllVisible = (isVisible: boolean) =>
    setDraft((cur) => (cur ?? []).map((s) => (s.isEngineerToggleable ? { ...s, isVisible } : s)));

  const resize = (sectionKey: string, columns: ReportSectionColumn[]) =>
    setDraft((cur) =>
      (cur ?? []).map((s) => (s.sectionKey === sectionKey ? { ...s, columns } : s)),
    );

  /**
   * Reorders composable sections among THEIR OWN slots.
   *
   * The structural sections hold their template positions: the reorder is
   * computed over the composable keys alone and then poured back into the slots
   * they already occupied. So content can be arranged freely, but nothing can be
   * moved above the header or below the signature block — which is exactly what
   * the backend enforces, reached here without ever building a rejected payload.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((cur) => {
      const list = cur ?? [];
      const composable = list.filter((s) => s.isEngineerToggleable);
      const oldIndex = composable.findIndex((s) => s.sectionKey === String(active.id));
      const newIndex = composable.findIndex((s) => s.sectionKey === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return list;

      const moved = arrayMove(composable, oldIndex, newIndex);
      let next = 0;
      return list.map((s) => (s.isEngineerToggleable ? moved[next++]! : s));
    });
  };

  // ── Read error ─────────────────────────────────────────────────────────────
  // A failure must never be shown as "this report has no sections".
  if (query.error) {
    const forbidden = isForbidden(query.error);
    return (
      <div className="space-y-2" data-testid="report-section-composer-error">
        {!hideHeading && <h3 className="text-sm font-semibold text-foreground">Report Sections</h3>}
        <p className="text-sm text-destructive">
          {forbidden
            ? 'You do not have permission to view the report sections.'
            : extractMsg(query.error as Error, 'Could not load the report sections.')}
        </p>
        {!forbidden && (
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="retry-report-sections"
          >
            <FontAwesomeIcon icon={faArrowRotateRight} className="h-3 w-3" />
            Retry
          </button>
        )}
      </div>
    );
  }

  if (query.isLoading || !composition || draft === null || saved === null) {
    return (
      <div className="space-y-2" data-testid="report-section-composer-loading">
        {!hideHeading && <h3 className="text-sm font-semibold text-foreground">Report Sections</h3>}
        <p className="text-sm text-muted-foreground">Loading sections…</p>
      </div>
    );
  }

  // Editable requires BOTH the report lifecycle (caller) and the server's own
  // verdict, which additionally accounts for the layout freeze.
  const locked = !composition.editable;
  const editable = canEdit && composition.editable && !saveForbidden;
  const dirty = !sameComposition(draft, saved);
  // Counts describe what the engineer actually controls. Including the four
  // structural sections would report "12 of 12" on a report where only eight
  // are the engineer's to choose.
  const composable = draft.filter((s) => s.isEngineerToggleable);
  const structural = draft.filter((s) => !s.isEngineerToggleable);
  const visibleCount = composable.filter((s) => s.isVisible).length;

  return (
    <div className="space-y-4" data-testid="report-section-composer">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!hideHeading && <h3 className="text-sm font-semibold text-foreground">Report Sections</h3>}
        <span className="text-xs text-muted-foreground" data-testid="section-selection-count">
          Selected: {visibleCount} of {composable.length}
        </span>
      </div>

      {locked && (
        <p
          className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-xs text-foreground"
          data-testid={
            composition.state === 'LEGACY' ? 'sections-legacy-notice' : 'sections-frozen-notice'
          }
        >
          <FontAwesomeIcon icon={faLock} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {composition.state === 'LEGACY'
              ? 'This report was generated before section composition existed. Its layout is preserved exactly as issued and cannot be changed.'
              : 'Report layout is locked after report generation. The sections below are the ones this report was issued with.'}
          </span>
        </p>
      )}

      {saveForbidden && (
        <p className="text-xs text-destructive" data-testid="sections-permission-notice">
          You do not have permission to change the report sections.
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAllVisible(true)}
            disabled={busy || visibleCount === composable.length}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="select-all-sections"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={() => setAllVisible(false)}
            disabled={busy || visibleCount === 0}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="clear-all-sections"
          >
            Clear all
          </button>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={composable.map((s) => s.sectionKey)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="space-y-1.5" data-testid="report-sections-list">
            {composable.map((section, i) => (
              <SortableSection
                key={section.sectionKey}
                section={section}
                index={i}
                canEdit={editable && !busy}
                busy={busy}
                onToggle={() => toggle(section.sectionKey)}
                onResize={(columns) => resize(section.sectionKey, columns)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {/*
        The template's own structure, listed so an engineer can see the whole
        report — with no checkbox, no drag handle and, since
        D-STRUCTURAL-WIDTH = B, no width editor either. Neither presence, order
        nor proportion is theirs to change on one report; all three belong to the
        MSE's Template Designer. The signature block is mandatory and always
        present.

        The API agrees independently: it now reports `resizable: false` for these
        sections, and rejects a width change on them even if this panel is
        bypassed. Nothing here infers the rule from the section key.
      */}
      {structural.length > 0 && (
        <div
          className="rounded-lg border border-dashed border-border px-3 py-2"
          data-testid="template-controlled-sections"
        >
          <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <FontAwesomeIcon icon={faLock} className="h-3 w-3" />
            Set by the report template — always included, and sized by the template
          </p>
          <ul className="mt-2 space-y-1.5">
            {structural.map((section) => (
              <li
                key={section.sectionKey}
                className="rounded-md border border-border bg-card px-3 py-1.5"
                data-testid={`template-section-${section.sectionKey}`}
              >
                <span className="text-sm text-foreground">{labelOf(section)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {visibleCount === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="sections-none-visible">
          No sections selected — the report will be effectively empty.
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => saveMutation.mutate(draft)}
            disabled={busy || !dirty}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="save-report-sections"
          >
            {busy ? 'Saving…' : 'Save sections'}
          </button>
          <button
            type="button"
            onClick={() => setDraft(saved)}
            disabled={busy || !dirty}
            className="rounded-md border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-muted disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="cancel-report-sections"
          >
            Cancel
          </button>
          {dirty && !busy && (
            <span className="text-xs text-muted-foreground" data-testid="sections-unsaved">
              Unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
}
